import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { EditorState } from '@tiptap/pm/state'
import { COLOR_ATTRIBUTE_NAME, TEXT_STYLE_MARK_NAME } from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import { applyFormattingSuggestion, TrackChangesUser } from './track-changes-plugin'

export type ColorCommandTrackingOptions = {
  getEnabled: () => boolean
  getCurrentUser: () => TrackChangesUser | null
}

function colorOfTextNode(node: ProseMirrorNode): string | null {
  const textStyleMark = node.marks.find((mark) => mark.type.name === TEXT_STYLE_MARK_NAME) ?? null
  return (textStyleMark?.attrs[COLOR_ATTRIBUTE_NAME] as string | null) ?? null
}

type PriorColor = { uniform: true; color: string | null } | { uniform: false }

function readPriorColor(state: EditorState, from: number, to: number): PriorColor {
  let color: string | null = null
  let initialized = false
  let uniform = true
  state.doc.nodesBetween(from, to, (node) => {
    if (!node.isText) {
      return true
    }
    const nodeColor = colorOfTextNode(node)
    if (!initialized) {
      color = nodeColor
      initialized = true
    } else if (nodeColor !== color) {
      uniform = false
    }
    return false
  })
  return uniform ? { uniform: true, color } : { uniform: false }
}

export function runColorCommandWithTracking(editor: Editor, options: ColorCommandTrackingOptions, color: string): boolean {
  const currentUser = options.getCurrentUser()
  const { from, to } = editor.state.selection
  if (!options.getEnabled() || currentUser === null || from === to) {
    return editor.chain().focus().setColor(color).run()
  }

  const prior = readPriorColor(editor.state, from, to)
  if (!prior.uniform) {
    return editor.chain().focus().setColor(color).run()
  }

  const priorColor = prior.color

  return editor
    .chain()
    .focus()
    .setColor(color)
    .command(({ tr, dispatch }) => {
      if (dispatch === undefined || !tr.docChanged) {
        return true
      }
      applyFormattingSuggestion(tr, { from, to }, currentUser, { added: new Set(), removed: new Set(), colorChange: { from: priorColor, to: color } })
      return true
    })
    .run()
}
